import { BadgeCheck, Check, CreditCard, Loader2, Mail, ShieldCheck, UserRound, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LearnMore } from "@/components/resources/LearnMore";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * Step-by-step bidder verification gate (L1-L4), journey-tracker style.
 * Facts only — the level is computed server-side, never claimed client-side.
 */
export const VerificationGate = ({ requiredLevel = 2, onSatisfied, onState }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({ phone: "", country: "" });
  const [emailSent, setEmailSent] = useState(false);
  const [devToken, setDevToken] = useState(null);
  const [code, setCode] = useState("");
  const [identityUnavailable, setIdentityUnavailable] = useState(false);

  const factsLevel = Math.min(requiredLevel, 3);
  const satisfied = v && v.level >= factsLevel;

  const refresh = useCallback(async () => {
    const { data } = await api.get("/verification/me");
    setV(data);
    setProfile((p) => ({ phone: p.phone || data.phone || "", country: p.country || data.country || "" }));
    onState?.(data);
    return data;
  }, [onState]);

  useEffect(() => {
    refresh().catch(() => setV(null));
  }, [refresh]);

  useEffect(() => {
    if (v && v.identity_status === "pending") {
      api.get("/verification/identity/status").then(({ data }) => { setV(data); onState?.(data); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(v)]);

  useEffect(() => {
    if (satisfied) onSatisfied?.(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satisfied]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/profile", {
        phone: profile.phone.trim(),
        country: profile.country.trim().toUpperCase(),
      });
      setV(data);
      onState?.(data);
    } catch {
      toast.error(t("vrf.profileError"));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/email/send");
      setEmailSent(true);
      if (data.dev_token) setDevToken(data.dev_token);
    } finally {
      setBusy(false);
    }
  };

  const confirmEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/email/confirm", { token: code.trim() });
      setV(data);
      onState?.(data);
      toast.success(t("vrf.emailVerified"));
    } catch {
      toast.error(t("vrf.codeInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const startCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/card/setup", {
        return_path: location.pathname + location.search,
      });
      window.location.assign(data.url);
    } catch {
      toast.error(t("vrf.cardError"));
      setBusy(false);
    }
  };

  const startIdentity = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verification/identity/start", {
        return_path: "/account/verification/return",
      });
      window.location.assign(data.url);
    } catch (err) {
      if (err?.response?.status === 503) {
        setIdentityUnavailable(true); // honest: activation pending, never fake a pass
      } else {
        toast.error(t("vrf.identityError"));
      }
      setBusy(false);
    }
  };

  if (!v) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="verification-gate-loading">
        <Loader2 className="w-5 h-5 animate-spin text-black/30" aria-hidden="true" />
      </div>
    );
  }

  const steps = [
    { key: "profile", icon: UserRound, done: v.profile_complete },
    { key: "email", icon: Mail, done: v.email_verified },
    ...(requiredLevel >= 2 ? [{ key: "card", icon: CreditCard, done: v.has_card }] : []),
    ...(requiredLevel >= 3
      ? [{ key: "identity", icon: ShieldCheck, done: v.identity_status === "verified", pending: v.identity_status === "pending" }]
      : []),
    ...(requiredLevel >= 4 ? [{ key: "approval", icon: Users, done: false, info: true }] : []),
  ];
  const activeIdx = steps.findIndex((s) => !s.done && !s.info);

  return (
    <div className="flex flex-col gap-1" data-testid="verification-gate">
      <div className="flex items-center gap-2 mb-3">
        <BadgeCheck className="w-4 h-4 text-[#1D4D3B]" aria-hidden="true" />
        <span className="text-sm font-semibold text-[#0A0A0A]">
          {t("vrf.gateTitle", { level: requiredLevel })}
        </span>
        <span className="ml-auto text-xs text-[#737373]" data-testid="verification-current-level">
          {t("vrf.yourLevel", { level: v.level })}
        </span>
        <LearnMore slug="registering-and-verifying-your-account" section="identity-verification" />
      </div>

      {steps.map((s, i) => {
        const active = i === activeIdx;
        const Icon = s.icon;
        return (
          <div
            key={s.key}
            className={`rounded-2xl border transition-colors duration-300 ${
              active ? "border-black/15 bg-white" : "border-black/5 bg-black/[0.015]"
            }`}
            data-testid={`gate-step-${s.key}`}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <span
                className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  s.done ? "bg-[#1D4D3B] text-white" : active ? "bg-[#0A0A0A] text-white" : "bg-black/5 text-black/30"
                }`}
              >
                {s.done ? <Check className="w-4 h-4" aria-hidden="true" /> : <Icon className="w-4 h-4" aria-hidden="true" />}
              </span>
              <div className="flex flex-col min-w-0">
                <span className={`text-sm font-medium ${s.done || active ? "text-[#0A0A0A]" : "text-black/40"}`}>
                  {t(`vrf.step_${s.key}`)}
                </span>
                <span className="text-xs text-[#737373]">{t(`vrf.step_${s.key}_desc`)}</span>
              </div>
              {s.done && <span className="ml-auto text-xs font-medium text-[#1D4D3B]" data-testid={`gate-step-${s.key}-done`}>{t("vrf.done")}</span>}
              {s.pending && !s.done && (
                <span className="ml-auto text-xs font-medium text-[#B45309]">{t("vrf.identityPending")}</span>
              )}
            </div>

            {active && !s.done && (
              <div className="px-4 pb-4 flex flex-col gap-3">
                {s.key === "profile" && (
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="flex flex-col gap-1.5 flex-1">
                      <Label htmlFor="vrf-phone" className="text-xs">{t("vrf.phone")}</Label>
                      <Input id="vrf-phone" data-testid="gate-phone-input" value={profile.phone}
                        onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="+31 6 12345678" className="rounded-xl h-10" />
                    </div>
                    <div className="flex flex-col gap-1.5 w-full sm:w-24">
                      <Label htmlFor="vrf-country" className="text-xs">{t("vrf.country")}</Label>
                      <Input id="vrf-country" data-testid="gate-country-input" value={profile.country} maxLength={2}
                        onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value.toUpperCase() }))}
                        placeholder="NL" className="rounded-xl h-10 uppercase" />
                    </div>
                    <Button onClick={saveProfile} disabled={busy || profile.phone.length < 5 || profile.country.length !== 2}
                      data-testid="gate-profile-save" className="rounded-full h-10 px-5 bg-[#0A0A0A] text-white hover:bg-[#2b2b2b]">
                      {t("common.save")}
                    </Button>
                  </div>
                )}

                {s.key === "email" && (
                  emailSent ? (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs text-[#737373]">{t("vrf.codeSent")}</p>
                      {devToken && (
                        <p className="text-[11px] text-[#B45309] bg-[#B45309]/[0.06] rounded-lg px-3 py-2" data-testid="gate-dev-token">
                          {t("vrf.devCode")}: <code className="font-mono">{devToken}</code>
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="gate-code-input"
                          placeholder={t("vrf.codePlaceholder")} className="rounded-xl h-10 flex-1" />
                        <Button onClick={confirmEmail} disabled={busy || !code.trim()} data-testid="gate-code-confirm"
                          className="rounded-full h-10 px-5 bg-[#0A0A0A] text-white hover:bg-[#2b2b2b]">
                          {t("vrf.confirm")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={sendEmail} disabled={busy} data-testid="gate-email-send"
                      className="rounded-full h-10 px-5 self-start bg-[#0A0A0A] text-white hover:bg-[#2b2b2b]">
                      {t("vrf.sendCode")}
                    </Button>
                  )
                )}

                {s.key === "card" && (
                  <>
                    <p className="text-xs text-[#737373]">{t("vrf.cardNote")}</p>
                    <Button onClick={startCard} disabled={busy} data-testid="gate-card-start"
                      className="rounded-full h-10 px-5 self-start bg-[#0A0A0A] text-white hover:bg-[#2b2b2b]">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t("vrf.addCard")}
                    </Button>
                  </>
                )}

                {s.key === "identity" && (
                  identityUnavailable ? (
                    <p className="text-xs text-[#B45309] bg-[#B45309]/[0.06] rounded-lg px-3 py-2" data-testid="gate-identity-unavailable">
                      {t("vrf.identityUnavailable")}
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-[#737373]">{t("vrf.identityNote")}</p>
                      <p className="text-[11px] text-[#737373]/80">{t("vrf.identityPrivacy")}</p>
                      <Button onClick={startIdentity} disabled={busy} data-testid="gate-identity-start"
                        className="rounded-full h-10 px-5 self-start bg-[#0A0A0A] text-white hover:bg-[#2b2b2b]">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t("vrf.verifyIdentity")}
                      </Button>
                    </>
                  )
                )}
              </div>
            )}
            {s.info && (
              <p className="px-4 pb-3 text-xs text-[#737373]" data-testid="gate-approval-note">{t("vrf.approvalNote")}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};
